// ============================================
// ARTIFACT 1: Enhanced Form Wizard Component
// ============================================
// File: src/app/shared/components/form/form-wizard/form-wizard-enhanced.component.ts
// Purpose: Main form wizard with collapsible sections and progressive disclosure
// Dependencies: PrimeNG 20, Angular Reactive Forms, Custom Media Components
//
// USAGE:
// 1. Copy this entire file to: src/app/shared/components/form/form-wizard/form-wizard-enhanced.component.ts
// 2. Ensure FormActionsBarComponent is in the same directory
// 3. Import and use in your feature components
//
// EXAMPLE:
// <app-form-wizard-enhanced
//   [config]="formConfig"
//   [initialData]="data"
//   [mode]="'create'"
//   (submitSuccess)="onSave($event)"
//   (cancel)="onCancel()"
// />
// ============================================

import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
// PrimeNG 20 Modules
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FormActionsBarComponent } from './form-actions-bar.component';
import {
  FormWizardFieldRegistryService,
} from './form-wizard-field-registry.service';
import { FormWizardSectionComponent } from './form-wizard-section.component';
import { FormWizardStateService } from './form-wizard-state.service';

// Services
import { ConfirmationService } from 'primeng/api';

// Models
import {
  FormWizardConfig,
  FormSection,
  EntityFormHandle,
  FormWizardSectionState,
} from './models/form-wizard.models';
import { getFormSectionId } from './form-wizard-section-id.utils';

@Component({
  selector: 'app-form-wizard-enhanced',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    FormActionsBarComponent,
    FormWizardSectionComponent
],
  providers: [
    ConfirmationService,
    FormWizardStateService,
    FormWizardFieldRegistryService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="form-wizard-enhanced"
      [class.form-wizard--loading]="showLoadingState()"
      [class.form-wizard--modal]="config?.layout === 'modal'"
    >

      @if (isSubmitting() && showLoadingSpinner) {
        <div class="form-wizard__loading" aria-live="polite" aria-busy="true">
          <p-progressSpinner />
        </div>
      }

      <div class="form-wizard__container" #wizardContainer data-scroll-region="form-content">
        @if (form) {
          <form [formGroup]="form" class="form-wizard__content">
            @for (section of visibleSections; track $index; let i = $index) {
              <app-form-wizard-section
                [section]="section"
                [sectionState]="getSectionState(i)"
                [form]="form"
                [layout]="config?.layout || 'default'"
                (toggle)="toggleSection($event)"
              />
            }
          </form>
        }
      </div>

      <p-confirmDialog />

      @if (config?.layout !== 'modal') {
        <div class="form-wizard__actions-region" data-actions-region="pinned">
          <app-form-actions-bar
            [isValid]="form?.valid || false"
            [isDirty]="form?.dirty || false"
            [isSubmitting]="isSubmitting()"
            [isEditMode]="isEditMode()"
            [submitButtonText]="config?.submitButtonText"
            [submitButtonIcon]="config?.submitButtonIcon"
            [cancelButtonText]="config?.cancelButtonText"
            [cancelButtonIcon]="config?.cancelButtonIcon"
            (save)="handleSubmit()"
            (cancel)="handleCancel()"
          />
        </div>
      }
    </div>
  `,
  styleUrls: ['./form-wizard.component.scss'],
})
export class FormWizardEnhancedComponent<T = any>
  implements OnInit, OnDestroy, OnChanges, EntityFormHandle<T> {
  @Input() config: FormWizardConfig<T> | null = null;
  @Input() initialData?: T;
  @Input() showValidationSummary = true;
  @Input() mode: 'create' | 'edit' | 'view' = 'create';
  @Input() persistLoadingClassOnSuccess = false;
  @Input() showLoadingSpinner = true;

  @Output() submitSuccess = new EventEmitter<T>();
  @Output() submitError = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  @Output() formChange = new EventEmitter<Partial<T>>();

  @ViewChild('wizardContainer') wizardContainer!: ElementRef;

  private readonly formState = inject(FormWizardStateService<T>);
  private confirmationService = inject(ConfirmationService);
  private cdr = inject(ChangeDetectorRef);
  private formSubscriptions$ = new Subject<void>();

  isSubmitting = signal(false);
  hasErrors = signal(false);
  isEditMode = signal(false);
  private readonly loadingClassHeldAfterSuccess = signal(false);
  readonly showLoadingState = computed(
    () => this.isSubmitting() || this.loadingClassHeldAfterSuccess(),
  );

  private allowNavigation = false;

  get form(): FormGroup | undefined {
    return this.formState.form;
  }

  get visibleSections(): FormSection<T>[] {
    return this.formState.visibleSections;
  }

  ngOnInit() {
    this.isEditMode.set(this.mode === 'edit' || !!this.initialData);
    this.initializeForm();
    this.setupBeforeUnloadHandler();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['persistLoadingClassOnSuccess'] && !this.persistLoadingClassOnSuccess) {
      this.loadingClassHeldAfterSuccess.set(false);
    }

    // If config or initialData changes significantly, re-initialize
    if (changes['config'] || (changes['initialData'] && !changes['initialData'].firstChange)) {
      this.isEditMode.set(this.mode === 'edit' || !!this.initialData);
      this.initializeForm();

      // Reset scroll position on change
      if (this.wizardContainer) {
        this.wizardContainer.nativeElement.scrollTop = 0;
      }
    }
  }

  ngOnDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.formSubscriptions$.next();
    this.formSubscriptions$.complete();
  }

  private initializeForm() {
    this.loadingClassHeldAfterSuccess.set(false);

    if (!this.config || !this.config.sections) {
      console.error('[FormWizardEnhanced] Config with sections is required');
      return;
    }

    this.resetFormSubscriptions();
    this.formState.initialize(this.config, this.initialData);

    const form = this.form;
    if (form && this.config.onInit) {
      this.config.onInit(form);
      this.formState.updateSectionStates();
    }

    this.setupListeners();
    this.cdr.markForCheck();
  }

  private resetFormSubscriptions() {
    this.formSubscriptions$.next();
    this.formSubscriptions$.complete();
    this.formSubscriptions$ = new Subject<void>();
  }

  private setupListeners() {
    const form = this.form;
    if (!form) {
      return;
    }

    // Global form changes
    form.valueChanges.pipe(takeUntil(this.formSubscriptions$)).subscribe((value) => {
      this.hasErrors.set(form.invalid);
      this.formState.updateSectionStates();
      this.formChange.emit(value);
      this.cdr.markForCheck();
    });

    // Individual field change listeners (for auto-calculation and reactions)
    this.formState.getAllFields().forEach(field => {
      if (field.onChange || this.config?.onChange) {
        const control = form.get(field.key);
        if (control) {
          control.valueChanges.pipe(takeUntil(this.formSubscriptions$)).subscribe(value => {
            // Per-field handler
            if (field.onChange) {
              field.onChange(value, form);
            }
            // Global handler with key
            if (this.config?.onChange) {
              this.config.onChange(value, field.key, form);
            }
          });
        }
      }
    });

    form.statusChanges.pipe(takeUntil(this.formSubscriptions$)).subscribe(() => {
      this.formState.updateSectionStates();
      this.cdr.markForCheck();
    });
  }

  private setupBeforeUnloadHandler() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  private handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.allowNavigation && this.form?.dirty) {
      event.preventDefault();
      event.returnValue =
        'Masz niezapisane zmiany. Czy na pewno chcesz opuścić stronę?';
      return event.returnValue;
    }
    return;
  };

  toggleSection(index: number) {
    this.formState.toggleSection(index);
    this.cdr.markForCheck();
  }

  expandSection(index: number) {
    this.formState.expandSection(index);
    this.cdr.markForCheck();
  }

  expandSectionById(sectionId: string): boolean {
    const sectionIndex = this.findVisibleSectionIndexById(sectionId);
    if (sectionIndex < 0) {
      return false;
    }

    this.expandSection(sectionIndex);
    return true;
  }

  getSectionState(index: number): FormWizardSectionState {
    return this.formState.getSectionState(index);
  }

  validateAndTouch(): boolean {
    const form = this.form;
    if (!form) {
      return false;
    }

    form.markAllAsTouched();
    this.formState.updateSectionStates();
    this.cdr.markForCheck();

    if (!form.valid) {
      this.hasErrors.set(true);
      this.formState.expandInvalidSections();
      this.scrollToFirstError();
      return false;
    }

    this.hasErrors.set(false);
    return true;
  }

  async submit() {
    const form = this.form;
    if (!form) {
      return;
    }

    if (!this.validateAndTouch()) {
      return;
    }

    this.isSubmitting.set(true);
    this.cdr.markForCheck();

    try {
      let data = this.getRawValue();
      let submitResponse: any;

      if (this.config?.beforeSubmit) {
        data = await this.config.beforeSubmit(data);
      }

      if (this.config?.onSubmit) {
        const result = this.config.onSubmit(data);

        if (result && typeof (result as any).then === 'function') {
          submitResponse = await result;
        } else if (result && typeof (result as any).subscribe === 'function') {
          submitResponse = await new Promise((resolve, reject) => {
            (result as any).subscribe({
              next: resolve,
              error: reject,
            });
          });
        } else {
          submitResponse = result;
        }
      }

      this.allowNavigation = true;

      if (this.persistLoadingClassOnSuccess) {
        this.loadingClassHeldAfterSuccess.set(true);
      }

      this.submitSuccess.emit(data);

      if (this.config?.afterSubmit) {
        this.config.afterSubmit(submitResponse, data);
      }

      if (this.config?.resetOnSubmit) {
        this.resetToInitial();
      }
    } catch (error) {
      console.error('❌ [FormWizardEnhanced] Error:', error);

      if (this.config?.onError) {
        this.config.onError(error);
      }

      this.loadingClassHeldAfterSuccess.set(false);
      this.submitError.emit(error);
    } finally {
      this.isSubmitting.set(false);
      this.cdr.markForCheck();
    }
  }

  async handleSubmit() {
    return this.submit();
  }

  handleCancel() {
    const isDirty = this.form?.dirty || false;
    const usesExternalCancelGuard = this.config?.confirmOnCancel === false;

    if (isDirty && !usesExternalCancelGuard) {
      this.confirmationService.confirm({
        message:
          'Czy na pewno chcesz anulować? Niezapisane zmiany zostaną utracone.',
        header: 'Potwierdzenie anulowania',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Anuluj i wyjdź',
        rejectLabel: 'Pozostań w formularzu',
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-outlined',
        accept: () => {
          this.allowNavigation = true;
          if (this.config?.onCancel) {
            this.config.onCancel();
          }
          this.cancel.emit();
        },
      });
    } else {
      // When the owner disables built-in cancel confirm, it owns the final leave decision.
      if (!usesExternalCancelGuard) {
        this.allowNavigation = true;
      }
      if (this.config?.onCancel) {
        this.config.onCancel();
      }
      this.cancel.emit();
    }
  }

  private scrollToFirstError() {
    setTimeout(() => {
      const firstError = this.wizardContainer?.nativeElement?.querySelector('.field--error');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  }

  scrollToSection(index: number) {
    setTimeout(() => {
      const section = this.wizardContainer?.nativeElement?.querySelector(
        `[data-section-index="${index}"]`
      );

      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  scrollToSectionById(sectionId: string): boolean {
    const sectionIndex = this.findVisibleSectionIndexById(sectionId);
    if (sectionIndex < 0) {
      return false;
    }

    this.scrollToSection(sectionIndex);
    return true;
  }

  getRawValue(): T {
    return (this.form?.getRawValue() || {}) as T;
  }

  getFormData(): T {
    return this.getRawValue();
  }

  setFormData(data: any) {
    if (this.form && data) {
      this.formState.setFormData(data);
      this.cdr.markForCheck();
    }
  }

  isValid(): boolean {
    return this.form?.valid || false;
  }

  isDirty(): boolean {
    return this.form?.dirty || false;
  }

  resetToInitial() {
    if (this.form) {
      this.formState.resetToInitial(this.initialData);
      this.hasErrors.set(false);
      this.isSubmitting.set(false);
      this.loadingClassHeldAfterSuccess.set(false);
      this.cdr.markForCheck();
    }
  }

  reset() {
    this.resetToInitial();
  }

  private findVisibleSectionIndexById(sectionId: string): number {
    return this.visibleSections.findIndex((section, index) => getFormSectionId(section, index) === sectionId);
  }
}
